/**
 * **One session on the trainer's booking calendar** — read once off `CalendarEventResponse`, and
 * everything the grid, the agenda and the detail panel ask of it.
 *
 * The block's marking is the mobile app's (`DayGridEvent`, owner's decisions of 13 September 2026):
 * HUE is the KIND — a training or a consultation — and TEXTURE is the AUTHOR — the client booked it,
 * or the trainer put it there. The one status the grid still draws is "running now", as a ring.
 *
 * What happened to an occurrence is `entities/calendar-event`'s `occurrenceStatusOf`; the instant
 * its slot names is `shared/lib/calendarDate`'s `occurrenceInstant`. WHERE it is drawn is R17 —
 * `entities/calendar-event`'s `occurrencePlacement`, asked on the TRAINER's clock: the slot, unless
 * the session was finished (or started) on another day. An occurrence with no hour is drawn at the
 * top of the day it names (`zonedMidnight`), as the mobile grid draws it, and prints no clock.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import {
  occurrenceKindOf,
  occurrencePlacement,
  occurrenceStatusOf,
  type OccurrenceKind,
  type OccurrenceStatusKey,
} from '~/entities/calendar-event';
import type { TrainWithMeModulesTrainingDtosResponsesCalendarEventResponse as CalendarEventResponse } from '~/shared/api/generated/model';
import { occurrenceInstant } from '~/shared/lib/calendarDate';

import { clockOf, wallReading, zonedMidnight, type CivilDay } from './trainerClock';

export type SessionKind = OccurrenceKind;
export type SessionAuthor = 'client' | 'trainer';

export interface BookingSession {
  readonly id: number;
  /** Who the session is with, as the wire names them — `null` where it names nobody. */
  readonly clientName: string | null;
  /** The client's id — the profile, the ledger and a forgiveness are all about this person. */
  readonly customerId: number;
  readonly kind: SessionKind;
  readonly author: SessionAuthor;
  readonly status: OccurrenceStatusKey | null;
  /** Called off — `Cancelled` or `LateCancelled`. Drawn on no grid and counted into no day. */
  readonly voided: boolean;
  /** The trainer-clock day it is drawn on, or `null` when the wire placed it nowhere readable. */
  readonly day: CivilDay | null;
  /** Whether it stands at an hour. Without one it is drawn at the top of its day, with no clock. */
  readonly hasHour: boolean;
  /** Where the grid draws it. For a session with no hour, its day's midnight. */
  readonly startMs: number | null;
  readonly endMs: number | null;
  /** The instant the RESERVATION starts — what "is it past its hour" is asked of. */
  readonly slotMs: number | null;
  readonly durationMinutes: number;
  /** `7:00–8:00` on the trainer's clock, or `null` with no hour. */
  readonly timeLabel: string | null;
  readonly startClock: string | null;
  readonly endClock: string | null;
  readonly isRemote: boolean;
  readonly isVideoCall: boolean;
  /** A video consultation's call address as the wire sent it, and where it came from. */
  readonly callLink: string | null;
  readonly callLinkOrigin: 'Manual' | 'GoogleMeet' | 'Standing' | null;
  /** What the session asked for its link; `Meet` with no link yet means Google is making it. */
  readonly callLinkSource: 'Meet' | 'Manual' | 'Standing' | 'None' | null;
  /** The backend knows calls (`callLink` is on the wire, even as null); an older one has no `PUT /call`. */
  readonly callsSupported: boolean;
  /** The plan's own name for the session, where it came from a plan. */
  readonly sessionName: string | null;
  /** An open reservation with nothing in it yet (R14 `EmptySlot`). */
  readonly emptySlot: boolean;
  /** R12's moment the client may still cancel for free until, as the wire sent it. */
  readonly cancelFreeUntil: string | null;
  /**
   * The three columns R10's credit rule keys on (`model/creditConsumption`), as the wire spells
   * them: where the session came from, its raw status, and whether a trainer is on its other side.
   */
  readonly origin: string | null;
  readonly wireStatus: string | null;
  readonly carriesTrainer: boolean;
  /** The workout behind it, when one is staged — what "Przejrzyj sesję" opens. */
  readonly executionId: number | null;
}

/**
 * The two origins that are the CLIENT's (R19): the client's own picker and the public link.
 * `Trainer`, `Plan`, `RecurringSeries` — and any origin this build was never taught — are the
 * trainer's: claiming the client booked it would claim knowledge the build does not have.
 */
const CLIENT_ORIGINS: ReadonlySet<string> = new Set(['SelfBooked', 'BookingLink']);

const VOIDED: ReadonlySet<OccurrenceStatusKey> = new Set<OccurrenceStatusKey>([
  'cancelled',
  'lateCancelled',
]);

const MINUTE_MS = 60_000;

/** Read one occurrence on the trainer's clock. */
export function readBookingSession(event: CalendarEventResponse, zone: string): BookingSession {
  const status = occurrenceStatusOf({
    status: event.status,
    derivedStatus: event.derivedStatus ?? null,
  });
  const slotMs = occurrenceInstant(event);
  const durationMinutes = Math.max(0, event.effectiveDurationMinutes);
  // R17 is the entity's, read on the trainer's clock — the one rule every calendar asks.
  const placed = occurrencePlacement(event, zone) ?? { day: null, atMs: null };
  const hasHour = placed.atMs !== null;
  const startMs = placed.atMs ?? (placed.day === null ? null : zonedMidnight(placed.day, zone));
  const endMs = startMs === null ? null : startMs + durationMinutes * MINUTE_MS;
  const startClock = hasHour && startMs !== null ? clockOf(wallReading(startMs, zone).minutes) : null;
  const endClock = hasHour && endMs !== null ? clockOf(wallReading(endMs, zone).minutes) : null;

  return {
    id: event.id,
    clientName: event.client?.fullName?.trim() || null,
    kind: occurrenceKindOf(event.type),
    author:
      event.bookedByClient === true || (event.origin != null && CLIENT_ORIGINS.has(event.origin))
        ? 'client'
        : 'trainer',
    status,
    voided: status !== null && VOIDED.has(status),
    day: placed.day,
    hasHour,
    startMs,
    endMs,
    slotMs,
    durationMinutes,
    timeLabel: startClock === null || endClock === null ? null : `${startClock}–${endClock}`,
    startClock,
    endClock,
    isRemote: event.isRemote,
    isVideoCall: event.isVideoCall === true,
    callLink: event.callLink ?? null,
    callLinkOrigin: event.callLinkOrigin ?? null,
    callLinkSource: event.callLinkSource ?? null,
    callsSupported: 'callLink' in event,
    sessionName: event.sessionName?.trim() || null,
    emptySlot: status === 'emptySlot',
    cancelFreeUntil: event.cancelFreeUntil ?? null,
    customerId: event.customerId,
    origin: event.origin ?? null,
    wireStatus: event.status ?? null,
    carriesTrainer: event.trainerId != null,
    executionId: event.workoutExecutionId ?? null,
  };
}

/**
 * The session's title — mobile's `eventTitle`: an open slot reads as one, a consultation as
 * "Konsultacja", a plan session by the plan's own name, anything else as "Trening".
 */
export function sessionTitle(session: BookingSession, t: (key: string) => string): string {
  if (session.emptySlot) return t('calendar.emptySlotTitle');
  if (session.kind === 'consultation') return t('calendar.consultTitle');
  return session.sessionName ?? t('calendar.adhocTitle');
}

/**
 * Is the session running at `nowMs`?
 *
 * A training runs when the client started it — the recorded `InProgress`, mobile's `isLive`, and
 * nothing else: a workout still `Upcoming` at its hour has not begun. A consultation is never
 * started in the app, so for it alone the clock answers: its reserved hour is under way.
 */
export function isLiveAt(session: BookingSession, nowMs: number): boolean {
  if (session.status === 'live') return true;
  if (session.kind !== 'consultation' || session.status !== 'scheduled') return false;
  if (!session.hasHour || session.startMs === null || session.endMs === null) return false;
  return nowMs >= session.startMs && nowMs < session.endMs;
}

/**
 * A consultation past its start that nobody has settled — the trainer's held / no-show verdict is
 * still owed. The mobile app's `consultAwaiting`: never "missed", because it most likely happened.
 * Asked of the RESERVED hour, as mobile's `slotInstant` is.
 */
export function awaitsVerdict(session: BookingSession, nowMs: number): boolean {
  return (
    session.kind === 'consultation' &&
    session.status === 'scheduled' &&
    session.slotMs !== null &&
    session.slotMs <= nowMs
  );
}

/** Whether a session belongs on the grid: not called off, and placed on a day. */
export function isDrawn(session: BookingSession): boolean {
  return !session.voided && session.day !== null && session.startMs !== null;
}
